import { contentItems, type ContentItem, type ContentType } from "@/mocks/content";
import { hotSearchWords, suggestionBank } from "@/mocks/hotsearch";

export type SortKey = "relevance" | "time";

export interface SearchParams {
  keyword?: string;
  type?: ContentType | "all";
  category?: string;
  sort?: SortKey;
  page?: number;
  pageSize?: number;
  minDuration?: number;
  maxDuration?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface SearchResult {
  items: ContentItem[];
  total: number;
  page: number;
  totalPages: number;
}

const parseDuration = (duration?: string): number => {
  if (!duration) return 0;
  const [m, s] = duration.split(":").map((n) => Number(n));
  if (Number.isNaN(m)) return 0;
  return m * 60 + (Number.isNaN(s) ? 0 : s);
};

const haystack = (item: ContentItem): string =>
  [item.title, item.summary, item.category, item.tags.join(" "), item.source, item.content.join(" ")]
    .join(" ")
    .toLowerCase();

export function matchKeyword(item: ContentItem, keyword?: string): boolean {
  const kw = (keyword || "").trim().toLowerCase();
  if (!kw) return true;
  const tokens = kw.split(/\s+/).filter(Boolean);
  const hay = haystack(item);
  return tokens.every((t) => hay.includes(t));
}

export function searchContent(params: SearchParams = {}): SearchResult {
  const { keyword = "", type = "all", category, sort = "relevance", page = 1, pageSize = 8 } = params;

  let list = contentItems.filter((item) => {
    if (type !== "all" && item.type !== type) return false;
    if (category && category !== "全部" && item.category !== category) return false;
    if (params.minDuration != null && item.type === "video") {
      const sec = parseDuration(item.duration);
      if (sec < params.minDuration) return false;
    }
    if (params.maxDuration != null && item.type === "video") {
      const sec = parseDuration(item.duration);
      if (sec > params.maxDuration) return false;
    }
    if (params.dateFrom && item.date < params.dateFrom) return false;
    if (params.dateTo && item.date > params.dateTo) return false;
    return matchKeyword(item, keyword);
  });

  list = list.sort((a, b) => {
    if (sort === "time") return b.date.localeCompare(a.date);
    return b.popularity - a.popularity;
  });

  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;

  return {
    items: list.slice(start, start + pageSize),
    total,
    page: safePage,
    totalPages,
  };
}

export function countByType(keyword: string, type: ContentType | "all"): number {
  return contentItems.filter(
    (item) => (type === "all" || item.type === type) && matchKeyword(item, keyword),
  ).length;
}

export function getItemById(type: string, id: string): ContentItem | undefined {
  return contentItems.find((item) => item.id === id && item.type === type);
}

export function getRelatedItems(item: ContentItem, count = 4): ContentItem[] {
  const sameType = contentItems.filter((c) => c.type === item.type && c.id !== item.id);
  const scored = sameType.map((c) => {
    const shared = c.tags.filter((t) => item.tags.includes(t)).length;
    const sameCat = c.category === item.category ? 3 : 0;
    return { c, score: shared * 2 + sameCat + c.popularity / 100 };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((s) => s.c);
}

export function getSuggestions(input: string, limit = 10): string[] {
  const kw = input.trim().toLowerCase();
  const pool = suggestionBank.map((s) => s.keyword);
  if (!kw) {
    return pool.slice(0, limit);
  }
  const matched = pool.filter((k) => k.toLowerCase().includes(kw));
  const withHot = [
    ...matched,
    ...hotSearchWords.map((h) => h.keyword).filter((k) => k.toLowerCase().includes(kw)),
  ];
  const unique = Array.from(new Set(withHot));
  return unique.length ? unique.slice(0, limit) : pool.slice(0, limit);
}

export function getRelatedSearches(keyword: string, limit = 8): string[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return hotSearchWords.slice(0, limit).map((h) => h.keyword);
  const tokens = kw.split(/\s+/).filter(Boolean);
  const related = hotSearchWords
    .filter((h) => tokens.some((t) => h.keyword.toLowerCase().includes(t) || t.includes(h.keyword.toLowerCase())))
    .map((h) => h.keyword);
  const fromBank = suggestionBank
    .map((s) => s.keyword)
    .filter((k) => tokens.some((t) => k.toLowerCase().includes(t)));
  const unique = Array.from(new Set([...related, ...fromBank]));
  if (unique.length >= limit) return unique.slice(0, limit);
  const filler = hotSearchWords.map((h) => h.keyword).filter((k) => !unique.includes(k));
  return [...unique, ...filler].slice(0, limit);
}